import { marked } from "marked";

import { sanitizeForDisplay } from "./richHtml.js";

/**
 * Agenda-Text (Markdown) → sanitisiertes HTML für Bühne, Vorschau und
 * Presenter (D9).
 *
 * Der Text ist Freitext des Leads auf Basis einer Vorlage und steht im Termin
 * vor dem Kunden. Deshalb ist `sanitizeForDisplay` hier eingebaut und nicht
 * Sache des Aufrufers: Wer `marked` direkt benutzt, umgeht den Riegel. Auf der
 * Bühne läuft das Ergebnis ein **zweites** Mal durch denselben Sanitizer —
 * die Sicherheitsinvariante des Bühnenkanals kennt nur `{ kind, title,
 * bodyHtml }` und vertraut dem Absender nicht (Spec D9).
 *
 * `gfm` wegen der Tabellen (`{{wartung.tabelle}}`, `{{budget.tabelle}}` kommen
 * als GFM-Tabellen aus der API), `breaks` weil der Lead im Textfeld schreibt
 * und dort ein Zeilenumbruch einer ist — niemand tippt zwei Leerzeichen ans
 * Zeilenende.
 *
 * `async: false` ist kein Beiwerk: Nur diese Überladung von `marked.parse`
 * liefert laut Typdefinition einen `string` statt `string | Promise<string>`.
 * So bleibt die Funktion synchron und ohne Zusicherung (`as string`) — was
 * auffiele, wenn eine spätere `marked`-Version das Verhalten änderte.
 *
 * Zusätzlich zu `sanitizeForDisplay` entfernt diese Funktion jedes `<img>`,
 * dessen `src` nicht mit `/api/v1/customer-view/attachments/` beginnt
 * (`stripDisallowedImages`). Agenda-Vorlagen sind von jedem Lead editierbar
 * und werden auf der Bühne anderer Leads vor deren Kunden angezeigt — ein
 * `![x](https://evil.example/px.gif)` im Vorlagentext wäre sonst ein
 * Tracking-Pixel oder ein Bild von fremdem Server, das beim Rendern lädt.
 * `sanitizeForDisplay` allein reicht hier nicht, weil `richHtml.ts` bewusst
 * Bilder über den Attachment-Proxy zulässt (für Jira-HTML, siehe
 * `RichHtml.tsx`) und diese Erlaubnis nicht auf den Ursprungs-Host
 * einschränkt.
 */
const ALLOWED_IMG_SRC_PREFIX = "/api/v1/customer-view/attachments/";

function stripDisallowedImages(html: string): string {
  if (!html.includes("<img")) {
    return html;
  }
  const doc = new DOMParser().parseFromString(html, "text/html");
  for (const img of Array.from(doc.images)) {
    const src = img.getAttribute("src") ?? "";
    if (!src.startsWith(ALLOWED_IMG_SRC_PREFIX)) {
      img.remove();
    }
  }
  return doc.body.innerHTML;
}

export function renderAgendaMarkdown(md: string): string {
  return stripDisallowedImages(
    sanitizeForDisplay(marked.parse(md, { gfm: true, breaks: true, async: false })),
  );
}
